import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useNavigationState } from "@/zustand/NavigationState";

/**
 * Opens the subscription plans view and clears the chatId from the URL.
 *
 * startSubscriptionView() sets activeChatId to null but does NOT change the URL.
 * If a chatId is present in the URL when subscription opens, we get a mismatch:
 * Zustand says null but the URL still shows the old chat. This means clicking
 * that same chat later is a navigate() no-op, so useChatRouteSync never fires
 * and activeChatId is never restored.
 *
 * By navigating to / (no chatId) here, the URL and Zustand stay in sync.
 * Any subsequent chat click changes the URL and useChatRouteSync handles it.
 */
interface StartSubscriptionOptions {
  /**
   * Use history replace instead of push. Set true when called from a
   * deep-link page (e.g. /subscription) so pressing Back doesn't bounce
   * the user between the page and its redirect target.
   */
  replace?: boolean;
}

export function useStartSubscriptionView() {
  const startSubscriptionView = useNavigationState(
    (s) => s.startSubscriptionView,
  );
  const navigate = useNavigate();

  return useCallback(
    (options?: StartSubscriptionOptions) => {
      startSubscriptionView();
      if (options?.replace) {
        navigate("/", { replace: true });
      } else {
        navigate("/");
      }
    },
    [startSubscriptionView, navigate],
  );
}
